<div class="parser-view">
  <!-- Region A — review header -->
  <header class="review-header">
    <button type="button" class="btn btn-link btn-sm back-link" (click)="backToReviewList()">
      <i class="fas fa-arrow-left me-1" aria-hidden="true"></i>{{ 'AI_PARSER_BACK_TO_INBOX' | translate }}
    </button>
    <div class="review-header-row">
      <div class="review-header-text">
        <div class="review-title-row">
          <h3>{{ reviewItem?.originalFilename || ('AI_PARSER_PARSED_ORDER' | translate) }}</h3>
          <div class="review-header-status" *ngIf="reviewItem">
            <lib-pdf-ai-parser-status-pill
              [status]="reviewItem.status"
              [parsing]="isParsing"
            ></lib-pdf-ai-parser-status-pill>
            <lib-pdf-ai-parser-status-pill
              *ngIf="reviewItem.autoParseDecision"
              [status]="reviewItem.autoParseDecision"
            ></lib-pdf-ai-parser-status-pill>
            <span class="erp-orders" *ngIf="reviewItem.status === 'DONE' && reviewItem.erpOrderIds?.length">
              <span class="erp-orders-label">{{ 'AI_PARSER_ERP_ORDER_NO' | translate }}</span>
              <a
                *ngFor="let erpId of reviewItem.erpOrderIds"
                class="erp-order-link"
                [routerLink]="['/orders', erpId]"
                [ngbTooltip]="'AI_PARSER_ERP_ORDER_TOOLTIP' | translate"
                >{{ erpId }}</a
              >
            </span>
          </div>
          <p class="review-meta" *ngIf="reviewItem">
            <span class="meta-item">
              <i
                class="fas"
                [class.fa-envelope]="orderSource === 'EMAIL'"
                [class.fa-arrow-up-from-bracket]="orderSource === 'UPLOAD'"
                aria-hidden="true"
              ></i>
              {{ (orderSource === 'EMAIL' ? 'AI_PARSER_RECEIVED_BY_EMAIL' : 'AI_PARSER_UPLOADED_MANUALLY') | translate }}
            </span>
            <ng-container *ngIf="reviewItem.fromEmail">
              <span class="meta-sep" aria-hidden="true">·</span>
              <span class="meta-item">{{ reviewItem.fromEmail }}</span>
            </ng-container>
            <ng-container *ngIf="reviewItem.receivedAt">
              <span class="meta-sep" aria-hidden="true">·</span>
              <span class="meta-item">{{ reviewItem.receivedAt | date: 'short' }}</span>
            </ng-container>
            <ng-container *ngIf="reviewItem.assignedConfigName">
              <span class="meta-sep" aria-hidden="true">·</span>
              <span class="meta-item">{{
                'AI_PARSER_CONFIG_NAMED' | translate: { name: reviewItem.assignedConfigName }
              }}</span>
            </ng-container>
          </p>
        </div>
      </div>
    </div>
    <p class="sender-auth-reason" *ngIf="senderAuthReasonKey">{{ senderAuthReasonKey | translate }}</p>
  </header>

  <!-- Error message (non-parse errors) -->
  <div *ngIf="errorMessage" class="parser-note note-danger mt-3">
    {{ errorMessage }}
  </div>

  <!-- Order submitted to the ERP in this session -->
  <div *ngIf="justSubmittedToErp" class="parser-note note-success mt-3">
    <i class="fas fa-check me-1" aria-hidden="true"></i>
    {{ 'AI_PARSER_TOAST_SUBMITTED_TITLE' | translate }}
    <ng-container *ngFor="let erpId of reviewItem?.erpOrderIds">
      <span class="note-sep" aria-hidden="true">·</span>
      <a class="note-order-link" [routerLink]="['/orders', erpId]">{{
        'AI_PARSER_ORDER_LINK' | translate: { id: erpId }
      }}</a>
    </ng-container>
  </div>

  <!-- Finalized outcome (DONE / REJECTED / ERROR) — shown above the order content -->
  <div
    *ngIf="reviewOutcome && !justSubmittedToErp"
    class="parser-note mt-3"
    [class.note-success]="reviewOutcome === 'DONE'"
    [class.note-danger]="reviewOutcome === 'ERROR'"
    [ngSwitch]="reviewOutcome"
  >
    <ng-container *ngSwitchCase="'DONE'">
      <i class="fas fa-check me-1" aria-hidden="true"></i>
      <ng-container *ngIf="reviewOutcomeDate as outcomeDate; else doneNoDate">
        {{ 'AI_PARSER_OUTCOME_DONE' | translate: { date: (outcomeDate | date: 'short') } }}
      </ng-container>
      <ng-template #doneNoDate>{{ 'AI_PARSER_STATUS_DONE' | translate }}</ng-template>
      <ng-container *ngFor="let erpId of reviewItem?.erpOrderIds">
        <span class="note-sep" aria-hidden="true">·</span>
        <a class="note-order-link" [routerLink]="['/orders', erpId]">{{
          'AI_PARSER_ORDER_LINK' | translate: { id: erpId }
        }}</a>
      </ng-container>
    </ng-container>
    <ng-container *ngSwitchCase="'REJECTED'">
      <strong class="note-title">{{ 'AI_PARSER_OUTCOME_REJECTED' | translate }}</strong>
      <p class="note-caption" *ngIf="reviewItem?.rejectionReason">{{ reviewItem?.rejectionReason }}</p>
    </ng-container>
    <ng-container *ngSwitchCase="'ERROR'">
      <strong class="note-title">{{ 'AI_PARSER_OUTCOME_FAILED' | translate }}</strong>
      <p class="note-caption" *ngIf="reviewItem?.errorMessage">{{ reviewItem?.errorMessage }}</p>
    </ng-container>
  </div>

  <!-- ERP submission outcome unknown — blocks reject/delete/resubmit until a human resolves it -->
  <div *ngIf="reviewItem?.status === 'SUBMIT_OUTCOME_UNKNOWN'" class="parser-note note-danger mt-3">
    <strong class="note-title">{{ 'AI_PARSER_OUTCOME_UNKNOWN' | translate }}</strong>
    <p class="note-caption">
      {{ 'AI_PARSER_OUTCOME_UNKNOWN_HINT' | translate: { date: (reviewItem?.submissionAttemptedAt | date: 'short') } }}
    </p>
    <p class="note-caption" *ngIf="reviewItem?.errorMessage">{{ reviewItem?.errorMessage }}</p>
    <button type="button" class="btn btn-outline-danger btn-sm mt-2" (click)="openResolveOutcomeModal()">
      {{ 'AI_PARSER_RESOLVE_OUTCOME' | translate }}…
    </button>
  </div>

  <!-- Region B — split view: document on the left, parsed order on the right -->
  <div class="split-view">
    <!-- Left — document panel -->
    <div class="document-panel" [class.dimmed]="isParsing">
      <div class="panel-label document-panel-header">
        <span>{{ 'AI_PARSER_DOCUMENT' | translate }}</span>
        <button
          *ngIf="attachmentBlob"
          type="button"
          class="btn btn-link btn-sm open-original"
          (click)="downloadOriginal()"
          [ngbTooltip]="'AI_PARSER_DOWNLOAD' | translate"
          [attr.aria-label]="'AI_PARSER_DOWNLOAD' | translate"
        >
          <i class="fas fa-download" aria-hidden="true"></i>
        </button>
      </div>
      <div class="preview-surface" [class.light]="!!spreadsheetPreview || !!imageUrl">
        <object *ngIf="pdfUrl" [data]="pdfUrl" type="application/pdf" width="100%" height="100%">
          <p class="pdf-fallback">
            {{ 'AI_PARSER_PDF_FALLBACK' | translate }}
            <a [href]="pdfUrl" target="_blank">{{ 'AI_PARSER_DOWNLOAD' | translate }}</a>
          </p>
        </object>

        <!-- Photographed/scanned order image -->
        <div *ngIf="imageUrl" class="image-preview">
          <img [src]="imageUrl" [alt]="'AI_PARSER_IMAGE_ALT' | translate" />
        </div>

        <!-- Excel/CSV preview: file parsed in the browser, one tab per sheet -->
        <div *ngIf="!pdfUrl && spreadsheetPreview" class="spreadsheet-preview">
          <ul *ngIf="spreadsheetPreview.length > 1" class="sheet-tabs">
            <li *ngFor="let sheet of spreadsheetPreview; let i = index">
              <button
                type="button"
                class="sheet-tab"
                [class.active]="i === activePreviewSheetIndex"
                (click)="activePreviewSheetIndex = i"
              >
                {{ sheet.name }}
              </button>
            </li>
          </ul>
          <div class="sheet-table-wrapper" *ngIf="activePreviewSheet as sheet">
            <table class="table table-sm table-striped table-bordered mb-0">
              <thead *ngIf="sheet.rows.length > 0">
                <tr>
                  <th *ngFor="let cell of sheet.rows[0]">{{ cell }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of sheet.rows | slice: 1 : sheet.visibleCount">
                  <td *ngFor="let cell of row">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
            <div *ngIf="sheet.rows.length === 0" class="preview-empty">{{ 'AI_PARSER_EMPTY_SHEET' | translate }}</div>
          </div>
          <div
            *ngIf="activePreviewSheet && activePreviewSheet.totalRowCount > activePreviewSheet.visibleCount"
            class="truncation-note"
          >
            <span>{{
              'AI_PARSER_PREVIEW_TRUNCATED'
                | translate: { shown: activePreviewSheet.visibleCount, total: activePreviewSheet.totalRowCount }
            }}</span>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 show-more-rows"
              (click)="showMoreSpreadsheetRows(activePreviewSheet)"
            >
              {{ 'AI_PARSER_SHOW_MORE_ROWS' | translate: { count: nextSpreadsheetRowChunk(activePreviewSheet) } }}
            </button>
          </div>
        </div>

        <div *ngIf="!pdfUrl && !imageUrl && !spreadsheetPreview && isPreviewLoading" class="text-center p-4">
          <span class="spinner-border spinner-border-sm text-light"></span>
        </div>
        <div *ngIf="!pdfUrl && !imageUrl && !spreadsheetPreview && !isPreviewLoading" class="preview-unavailable">
          <i class="fas fa-file fa-2x mb-2 d-block" aria-hidden="true"></i>
          <p class="mb-0">{{ 'AI_PARSER_PREVIEW_UNAVAILABLE' | translate }}</p>
          <small *ngIf="reviewItem?.originalFilename">{{ reviewItem?.originalFilename }}</small>
        </div>
      </div>
    </div>

    <!-- Right — parse setup + results -->
    <div class="results-panel">
      <!-- Parse theater: indeterminate accent bar pinned to the top of the panel -->
      <div *ngIf="isParsing" class="parse-progress-bar" aria-hidden="true"></div>

      <section class="setup-panel">
        <div class="setup-panel-header">
          <div class="panel-label">{{ 'AI_PARSER_SETUP' | translate }}</div>
          <p class="source-legend" *ngIf="response?.order && sourceLegend.length > 0">
            <ng-container *ngFor="let entry of sourceLegend; let last = last">
              <span class="legend-item"
                ><i class="fas" [ngClass]="entry.icon" aria-hidden="true"></i>{{ entry.labelKey | translate }}</span
              >
              <span class="legend-sep" *ngIf="!last" aria-hidden="true">·</span>
            </ng-container>
          </p>
        </div>

        <div class="ai-card setup-card">
          <!-- Once a parse has produced an order the summary line stays as the card's disclosure header -->
          <div *ngIf="canCollapseSetup" class="setup-summary">
            <span class="setup-summary-text">
              <ng-container *ngIf="selectedConfigName">
                {{ 'AI_PARSER_PARSED_WITH' | translate: { name: selectedConfigName } }}
              </ng-container>
              <ng-container *ngIf="!selectedConfigName">
                {{ 'AI_PARSER_PARSED_NO_CONFIG' | translate }}
              </ng-container>
              <ng-container *ngIf="response?.success && editableRows.length > 0">
                <span class="summary-sep" aria-hidden="true">·</span>
                <span class="setup-summary-count">
                  {{
                    (editableRows.length === 1 ? 'AI_PARSER_ROWS_EXTRACTED_ONE' : 'AI_PARSER_ROWS_EXTRACTED')
                      | translate: { count: editableRows.length }
                  }}
                </span>
              </ng-container>
              <ng-container *ngIf="reviewItem?.manualInputUsed">
                <span class="summary-sep" aria-hidden="true">·</span>
                <span class="setup-summary-count">{{ 'AI_PARSER_MANUAL_INPUT_USED' | translate }}</span>
              </ng-container>
            </span>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm setup-summary-edit"
              (click)="setupCollapsed = !setupCollapsed"
              [attr.aria-expanded]="!setupCollapsed"
            >
              <i class="fas fa-chevron-down setup-summary-caret" [class.open]="!setupCollapsed" aria-hidden="true"></i
              >{{ (setupCollapsed ? 'AI_PARSER_EDIT_SETUP' : 'AI_PARSER_HIDE_SETUP') | translate }}
            </button>
          </div>

          <!-- Kept in the DOM and collapsed by grid height, so opening the settings animates instead
               of dropping 800px of form into the page in one frame. `inert` keeps the hidden fields
               out of the tab order. -->
          <div class="collapsible" [class.collapsible--open]="!setupCollapsed" [attr.inert]="setupCollapsed ? '' : null">
            <div class="collapsible-inner">
              <div class="setup-body">
                <!-- Config row -->
                <div class="mb-3">
                  <label class="form-label" for="configSelect">{{ 'AI_PARSER_CONFIGURATION' | translate }}</label>
                  <div class="config-row">
                    <select
                      id="configSelect"
                      class="form-select form-select-sm"
                      [ngModel]="selectedConfigName"
                      (ngModelChange)="onConfigSelected($event)"
                      [disabled]="isLoadingConfigs"
                    >
                      <option value="">{{ 'AI_PARSER_CONFIG_NONE' | translate }}</option>
                      <option *ngFor="let c of configs; trackBy: trackByConfigName" [value]="c.name">
                        {{ c.name }}{{ c.suggestedDeliveryCustomerId ? ' · ' + c.suggestedDeliveryCustomerId : '' }}
                      </option>
                    </select>
                    <div class="config-actions" role="group">
                      <button
                        type="button"
                        class="icon-btn config-save"
                        [class.has-unsaved]="hasUnsavedConfigChanges"
                        [disabled]="!selectedConfigName || isSavingConfig"
                        (click)="saveConfig()"
                        [ngbTooltip]="
                          (hasUnsavedConfigChanges ? 'AI_PARSER_CONFIG_UNSAVED' : 'AI_PARSER_CONFIG_SAVE') | translate
                        "
                        [attr.aria-label]="'AI_PARSER_CONFIG_SAVE' | translate"
                      >
                        <i class="fas fa-save" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="icon-btn"
                        [disabled]="isSavingConfig"
                        (click)="createConfig()"
                        [ngbTooltip]="'AI_PARSER_CONFIG_NEW' | translate"
                        [attr.aria-label]="'AI_PARSER_CONFIG_NEW' | translate"
                      >
                        <i class="fas fa-plus" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        class="icon-btn icon-btn--danger"
                        [disabled]="!selectedConfigName"
                        (click)="deleteSelectedConfig()"
                        [ngbTooltip]="'AI_PARSER_CONFIG_DELETE' | translate"
                        [attr.aria-label]="'AI_PARSER_CONFIG_DELETE' | translate"
                      >
                        <i class="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- AI instructions -->
                <div class="mb-3">
                  <label for="prompt" class="form-label">{{ 'AI_PARSER_AI_INSTRUCTIONS' | translate }}</label>
                  <textarea
                    id="prompt"
                    class="form-control"
                    [(ngModel)]="prompt"
                    rows="2"
                    [placeholder]="'AI_PARSER_AI_INSTRUCTIONS_PLACEHOLDER' | translate"
                  ></textarea>
                  <div class="field-help">{{ 'AI_PARSER_AI_INSTRUCTIONS_HELP' | translate }}</div>
                </div>

                <!-- Delivery / billing customer -->
                <div class="mb-3">
                  <label for="deliveryCustomerId" class="form-label">{{ 'AI_PARSER_DELIVERY_CUSTOMER' | translate }}</label>
                  <input
                    type="text"
                    autocomplete="off"
                    data-bwignore="true"
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                    id="deliveryCustomerId"
                    class="form-control"
                    [(ngModel)]="deliveryCustomerId"
                    (ngModelChange)="onParamCustomerIdChanged('delivery')"
                    [placeholder]="'AI_PARSER_CUSTOMER_ID_PLACEHOLDER' | translate"
                    [ngbTypeahead]="searchCustomersByName"
                    [resultFormatter]="customerSearchFormatter"
                    [inputFormatter]="customerIdFormatter"
                    (selectItem)="onParamCustomerPicked('delivery', $event)"
                  />
                  <div class="resolved-name" *ngIf="deliveryCustomerName">{{ deliveryCustomerName }}</div>
                  <div class="resolved-name" *ngIf="deliveryCustomerName === ''">
                    {{ 'AI_PARSER_UNKNOWN_CUSTOMER' | translate }}
                  </div>
                </div>
                <div class="mb-3">
                  <label for="billingCustomerId" class="form-label">{{ 'AI_PARSER_BILLING_CUSTOMER' | translate }}</label>
                  <input
                    type="text"
                    autocomplete="off"
                    data-bwignore="true"
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                    id="billingCustomerId"
                    class="form-control"
                    [(ngModel)]="billingCustomerId"
                    (ngModelChange)="onParamCustomerIdChanged('billing')"
                    [placeholder]="'AI_PARSER_CUSTOMER_ID_PLACEHOLDER' | translate"
                    [ngbTypeahead]="searchCustomersByName"
                    [resultFormatter]="customerSearchFormatter"
                    [inputFormatter]="customerIdFormatter"
                    (selectItem)="onParamCustomerPicked('billing', $event)"
                  />
                  <div class="resolved-name" *ngIf="billingCustomerName">{{ billingCustomerName }}</div>
                  <div class="resolved-name" *ngIf="billingCustomerName === ''">
                    {{ 'AI_PARSER_UNKNOWN_CUSTOMER' | translate }}
                  </div>
                </div>

                <!-- Sender emails under a collapsed "Automatic matching" disclosure -->
                <div class="disclosure mb-3">
                  <button
                    type="button"
                    class="disclosure-toggle"
                    (click)="senderMatchingOpen = !senderMatchingOpen"
                    [attr.aria-expanded]="senderMatchingOpen"
                  >
                    <i class="fas fa-chevron-right" [class.open]="senderMatchingOpen" aria-hidden="true"></i>
                    {{ 'AI_PARSER_AUTOMATIC_MATCHING' | translate }}
                  </button>
                  <div *ngIf="senderMatchingOpen" class="disclosure-body">
                    <label for="senderEmails" class="form-label">{{ 'AI_PARSER_SENDER_EMAILS' | translate }}</label>
                    <input
                      type="text"
                      autocomplete="off"
                      data-bwignore="true"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      id="senderEmails"
                      class="form-control"
                      [(ngModel)]="senderEmails"
                      [placeholder]="'AI_PARSER_SENDER_EMAILS_PLACEHOLDER' | translate"
                    />
                    <div class="field-help">{{ 'AI_PARSER_SENDER_EMAILS_HELP' | translate }}</div>
                  </div>
                </div>

                <!-- Parse failure note -->
                <div *ngIf="parseErrorMessage !== null" class="parser-note note-danger parse-failed-note">
                  <strong class="note-title">{{ 'AI_PARSER_PARSE_FAILED' | translate }}</strong>
                  <p class="note-caption" *ngIf="parseErrorMessage">{{ parseErrorMessage }}</p>
                  <button type="button" class="btn btn-outline-secondary btn-sm" [disabled]="isLoading" (click)="submit()">
                    {{ 'AI_PARSER_TRY_AGAIN' | translate }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Manual input and the configuration above it are both inputs to the re-parse. Not inside
               setup-body, so it survives the per-sender setup collapsing. -->
          <div class="disclosure manual-input-block">
            <button
              type="button"
              class="disclosure-toggle"
              *ngIf="reviewItemId"
              (click)="manualInputOpen = !manualInputOpen"
              [attr.aria-expanded]="manualInputOpen"
            >
              <i class="fas fa-chevron-right" [class.open]="manualInputOpen" aria-hidden="true"></i>
              <i class="fas fa-pen" aria-hidden="true"></i>
              {{ 'AI_PARSER_MANUAL_INPUT' | translate }}
            </button>
            <div
              class="collapsible"
              *ngIf="reviewItemId"
              [class.collapsible--open]="manualInputOpen"
              [attr.inert]="manualInputOpen ? null : ''"
            >
              <div class="collapsible-inner">
                <div class="disclosure-body">
                  <div class="field-help mb-2">{{ 'AI_PARSER_MANUAL_INPUT_HELP' | translate }}</div>
                  <textarea
                    id="manualInput"
                    class="form-control"
                    [class.manual-input-armed]="useManualInput"
                    [attr.aria-label]="'AI_PARSER_MANUAL_INPUT' | translate"
                    [(ngModel)]="manualInput"
                    rows="8"
                    [readonly]="isReadOnly"
                    [placeholder]="'AI_PARSER_MANUAL_INPUT_PLACEHOLDER' | translate"
                  ></textarea>
                  <label class="manual-input-toggle mt-2">
                    <input type="checkbox" [(ngModel)]="useManualInput" name="useManualInput" [disabled]="isReadOnly" />
                    <span>{{ 'AI_PARSER_MANUAL_INPUT_SEND' | translate }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Outside the card: the parse runs everything the card holds, configuration and manual
             input alike, so it is the section's action and not the manual input's button. -->
        <div class="parse-action">
          <span
            class="d-inline-block"
          >
            <button
              type="button"
              class="btn btn-primary parse-btn"
              [disabled]="isLoading"
              (click)="submit()"
            >
              <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isLoading" class="fas fa-wand-magic-sparkles me-2" aria-hidden="true"></i>
              {{ parseButtonLabelKey | translate }}
            </button>
          </span>
        </div>
      </section>

      <!-- Parse theater: rotating status line + skeleton table -->
      <div *ngIf="isParsing" class="parse-theater" aria-live="polite">
        <div class="parse-status">
          <i class="fas fa-wand-magic-sparkles" aria-hidden="true"></i>
          <span>{{ parsePhaseKey || '' | translate }}</span>
        </div>
        <div class="skeleton-table" aria-hidden="true">
          <div class="skeleton-header"></div>
          <div class="skeleton-row" *ngFor="let _ of skeletonRows; let i = index" [style.--skeleton-i]="i">
            <span class="skeleton-bar sk-60"></span>
            <span class="skeleton-bar sk-30"></span>
            <span class="skeleton-bar sk-15"></span>
            <span class="skeleton-bar sk-15"></span>
          </div>
        </div>
      </div>

      <!-- Results -->
      <ng-container *ngIf="!isParsing">
        <!-- AWAITING_CONFIGURATION pre-parse: nothing parsed yet, setup panel is the CTA -->
        <div *ngIf="!response && reviewItem?.status === 'AWAITING_CONFIGURATION'" class="needs-setup-empty">
          <i class="fas fa-wand-magic-sparkles" aria-hidden="true"></i>
          <p>{{ 'AI_PARSER_NEEDS_SETUP_EMPTY' | translate }}</p>
        </div>
        <ng-container *ngTemplateOutlet="resultsBlock"></ng-container>
      </ng-container>
    </div>
  </div>
</div>

<!-- Shared parse results: success caption, warnings, order header grid and rows table -->
<ng-template #resultsBlock>
  <div *ngIf="response" class="results">
    <div *ngIf="!response.success" class="parser-note note-warning">
      {{ response.message }}
    </div>

    <div *ngIf="response.order" class="order-result">
      <div class="panel-label">{{ 'AI_PARSER_ORDER_SECTION' | translate }}</div>
      <div class="ai-card order-card">
        <!-- The model's own notes about this parse: read before the rows, never folded away. -->
        <div class="parse-notes" *ngIf="orderLevelWarnings.length">
          <strong class="parse-notes-title">{{ 'AI_PARSER_NOTES' | translate }}</strong>
          <ul class="parse-notes-list mb-0">
            <li *ngFor="let warning of orderLevelWarnings">{{ warningText(warning) }}</li>
          </ul>
        </div>

        <ng-container *ngTemplateOutlet="customerFields"></ng-container>

        <ng-container *ngTemplateOutlet="orderMetadataBlock"></ng-container>

        <ng-container *ngTemplateOutlet="rowsTable"></ng-container>

        <div *ngIf="reviewItem?.status === 'AWAITING_REVIEW' && trustStatus" class="trust-card">
          <div class="trust-progress" *ngIf="trustReason === 'TRUST_RAMP'">
            <div class="trust-progress-track" role="progressbar" [attr.aria-valuenow]="trustProgressCurrent" aria-valuemin="0" [attr.aria-valuemax]="trustProgressTarget">
              <div class="trust-progress-fill" [style.width.%]="trustProgressPercent"></div>
            </div>
          </div>
          <div class="trust-card-head">
            <!-- The strict single-line header row -->
            <div class="trust-card-head-row">
              <div class="trust-accounts">
                <span class="trust-account" *ngIf="trustStatus?.delivery as delivery">
                  <i class="fas fa-building" aria-hidden="true"></i>
                  <span class="trust-account-role" *ngIf="!trustSingleCustomer">{{ 'AI_PARSER_DELIVERY_CUSTOMER' | translate }}</span>
                  <span class="trust-account-name" *ngIf="trustCustomerNames[delivery.customerId]">{{ trustCustomerNames[delivery.customerId] }}</span>
                  <span class="trust-account-id">#{{ delivery.customerId }}</span>
                </span>
                <span class="trust-account" *ngIf="!trustSingleCustomer && trustStatus?.billing as billing">
                  <i class="fas fa-building" aria-hidden="true"></i>
                  <span class="trust-account-role">{{ 'AI_PARSER_BILLING_CUSTOMER' | translate }}</span>
                  <span class="trust-account-name" *ngIf="trustCustomerNames[billing.customerId]">{{ trustCustomerNames[billing.customerId] }}</span>
                  <span class="trust-account-id">#{{ billing.customerId }}</span>
                </span>
              </div>

              <span class="trust-card-title" [ngSwitch]="trustReason">
                <ng-container *ngSwitchCase="'TRUST_RAMP'">{{ 'AI_PARSER_TRUST_BANNER_RAMP_TITLE' | translate }}</ng-container>
                <ng-container *ngSwitchCase="'PERIODIC_CHECK'">{{ 'AI_PARSER_TRUST_BANNER_PERIODIC_TITLE' | translate }}</ng-container>
                <ng-container *ngSwitchDefault>{{ 'AI_PARSER_TRUST_BANNER_CLEAR_TITLE' | translate }}</ng-container>
              </span>

              <!-- Counter -->
              <div class="trust-card-head-right">
                <span class="trust-progress-pill" *ngIf="trustReason === 'TRUST_RAMP'">{{ trustProgressCurrent }} / {{ trustProgressTarget }}</span>
              </div>
            </div>

            <div class="trust-card-foot-row">
              <p class="trust-card-warning">
                {{ 'AI_PARSER_TRUST_BANNER_EDIT_WARNING' | translate }}
              </p>

              <button
                type="button"
                class="btn btn-outline-secondary btn-sm setup-summary-edit"
                (click)="trustCardCollapsed = !trustCardCollapsed"
                [attr.aria-expanded]="!trustCardCollapsed"
              >
                <i class="fas fa-chevron-down setup-summary-caret" [class.open]="!trustCardCollapsed" aria-hidden="true"></i>
                {{ (trustCardCollapsed ? 'AI_PARSER_SHOW_DETAILS' : 'AI_PARSER_HIDE_DETAILS') | translate }}
              </button>
            </div>
          </div>
          <div class="trust-card-collapse" *ngIf="!trustCardCollapsed">
            <ul class="trust-detail-list" *ngIf="trustReason === 'TRUST_RAMP'">
              <li *ngIf="trustRemaining > 0" [innerHTML]="trustRemainingText"></li>
              <li [innerHTML]="trustRampAfterExplainer"></li>
            </ul>

            <ul class="trust-counter-list" *ngIf="!trustSingleCustomer">
              <li *ngFor="let counter of trustCounterRows">
                <span class="trust-counter-role">{{ counter.labelKey | translate }}</span>
                <span class="trust-counter-id">#{{ counter.customerId }}</span>
                <span class="trust-counter-value">{{ counter.cleanSubmissions }} / {{ trustProgressTarget }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<!-- Order header fields as a 2-column definition grid with provenance glyphs -->
<ng-template #customerFields>
  <div class="order-header-grid">
    <div class="def-field">
      <span class="def-label">{{ 'AI_PARSER_CUSTOMER_ORDER_NO' | translate }}</span>
      <span class="def-value" [class.def-empty]="!customerOrderNumber">{{ customerOrderNumber || '—' }}</span>
    </div>
    <div class="def-field ai-parser-field">
      <label class="def-label" for="editableDeliveryDate">
        {{ 'AI_PARSER_DELIVERY_DATE' | translate }}
        <lib-pdf-ai-parser-source-glyph
          [source]="deliveryDateSource"
          [configName]="selectedConfigName"
        ></lib-pdf-ai-parser-source-glyph>
      </label>
      <div class="date-field">
        <input
          type="text"
          autocomplete="off"
          data-bwignore="true"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          id="editableDeliveryDate"
          name="editableDeliveryDate"
          class="form-control form-control-sm"
          [class.is-invalid]="deliveryDateInvalid"
          placeholder="yyyy-mm-dd"
          ngbDatepicker
          #deliveryDatePicker="ngbDatepicker"
          container="body"
          [ngModel]="deliveryDateStruct"
          (ngModelChange)="onDeliveryDateChange($event)"
          [disabled]="isReadOnly"
        />
        <button
          type="button"
          class="date-toggle"
          [disabled]="isReadOnly"
          (click)="deliveryDatePicker.toggle()"
          [attr.aria-label]="'AI_PARSER_DELIVERY_DATE' | translate"
        >
          <i class="fas fa-calendar-alt" aria-hidden="true"></i>
        </button>
      </div>
      <div class="field-help" *ngFor="let warning of warningsFor('deliveryDate')">{{ warningText(warning) }}</div>
    </div>
    <div class="def-field ai-parser-field">
      <label class="def-label" for="editableDeliveryCustomerId">
        {{ 'AI_PARSER_DELIVERY_CUSTOMER' | translate }}
        <lib-pdf-ai-parser-source-glyph
          [source]="deliveryCustomerIdSource"
          [configName]="selectedConfigName"
        ></lib-pdf-ai-parser-source-glyph>
      </label>
      <input
        type="text"
        autocomplete="off"
        data-bwignore="true"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        id="editableDeliveryCustomerId"
        class="form-control form-control-sm"
        [(ngModel)]="editableDeliveryCustomerId"
        (ngModelChange)="onEditableDeliveryCustomerIdChanged()"
        [placeholder]="'AI_PARSER_CUSTOMER_ID_PLACEHOLDER' | translate"
        [readonly]="isReadOnly"
        [disabled]="isPreparingSubmit"
        [ngbTypeahead]="searchCustomersByName"
        [resultFormatter]="customerSearchFormatter"
        [inputFormatter]="customerIdFormatter"
        (selectItem)="onDeliveryCustomerPicked($event)"
      />
      <div class="resolved-name" *ngIf="editableDeliveryCustomerName">{{ editableDeliveryCustomerName }}</div>
      <div class="resolved-name" *ngIf="editableDeliveryCustomerName === ''">
        {{ 'AI_PARSER_UNKNOWN_CUSTOMER' | translate }}
      </div>
      <div class="field-help customer-from-config" *ngIf="documentDeliveryCustomerId">
        {{ 'AI_PARSER_CUSTOMER_FROM_CONFIG' | translate: { id: documentDeliveryCustomerId } }}
      </div>
      <div class="field-help" *ngFor="let warning of warningsFor('deliveryCustomerId')">{{ warningText(warning) }}</div>
    </div>
    <div class="def-field ai-parser-field">
      <label class="def-label" for="editableBillingCustomerId">
        {{ 'AI_PARSER_BILLING_CUSTOMER' | translate }}
        <lib-pdf-ai-parser-source-glyph
          [source]="billingCustomerIdSource"
          [configName]="selectedConfigName"
        ></lib-pdf-ai-parser-source-glyph>
      </label>
      <input
        type="text"
        autocomplete="off"
        data-bwignore="true"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        id="editableBillingCustomerId"
        class="form-control form-control-sm"
        [(ngModel)]="editableBillingCustomerId"
        (ngModelChange)="hasPendingChanges = true; editVersion = editVersion + 1; recomputeCustomerSources()"
        [placeholder]="'AI_PARSER_CUSTOMER_ID_PLACEHOLDER' | translate"
        [readonly]="isReadOnly"
        [ngbTypeahead]="searchCustomersByName"
        [resultFormatter]="customerSearchFormatter"
        [inputFormatter]="customerIdFormatter"
        (selectItem)="onBillingCustomerPicked($event)"
      />
      <div class="field-help customer-from-config" *ngIf="documentBillingCustomerId">
        {{ 'AI_PARSER_CUSTOMER_FROM_CONFIG' | translate: { id: documentBillingCustomerId } }}
      </div>
      <div class="field-help" *ngFor="let warning of warningsFor('billingCustomerId')">{{ warningText(warning) }}</div>
    </div>
  </div>
  <!-- The configuration's customer replaced the document's; the backend refuses without this.
       Outside the two-column grid so the bar spans the full width, as the other confirm bars do. -->
  <div *ngIf="customerOverridden && !reviewFinalized" class="deviation-bar metadata-confirm">
    <label class="commit-override">
      <input type="checkbox" [(ngModel)]="confirmCustomerOverride" name="confirmCustomerOverride" />
      <span>{{ 'AI_PARSER_CUSTOMER_OVERRIDE_CONFIRM' | translate }}</span>
    </label>
  </div>
</ng-template>

<!--
  Order metadata: the order's header and delivery-address fields, in the two tabs the reviewer
  already knows from the Edit Order modal. Three signals stay apart — the provenance glyph on the
  label says where the value came from, the amber dot says it differs from the customer's
  registered value, and the note under the field quotes what the document said when the value
  matched no master-data row at all.
-->
<ng-template #orderMetadataBlock>
  <section class="order-metadata">
    <span
      class="d-inline-block"
      [ngbTooltip]="'AI_PARSER_ORDER_METADATA_LOCKED_OPEN' | translate"
      tooltipClass="ai-parser-tooltip"
      [disableTooltip]="!orderMetadataNeedsAttention"
    >
      <button
        type="button"
        class="metadata-toggle"
        [disabled]="orderMetadataNeedsAttention"
        (click)="orderMetadataOpen = !orderMetadataOpen"
        [attr.aria-expanded]="orderMetadataExpanded"
      >
        <i class="fas fa-chevron-right" [class.open]="orderMetadataExpanded" aria-hidden="true"></i>
        <h4 class="metadata-heading">{{ 'AI_PARSER_ORDER_METADATA' | translate }}</h4>
      </button>
    </span>
    <div
      class="collapsible"
      [class.collapsible--open]="orderMetadataExpanded"
      [attr.inert]="orderMetadataExpanded ? null : ''"
    >
      <div class="collapsible-inner">
    <div class="field-help metadata-help">{{ 'AI_PARSER_ORDER_METADATA_HELP' | translate }}</div>

    <!-- Deviations need a stance before the backend will accept the order; it refuses without this. -->
    <div *ngIf="hasMetadataDeviations && !reviewFinalized" class="deviation-bar metadata-confirm">
      <label class="commit-override">
        <input
          type="checkbox"
          [(ngModel)]="confirmOrderMetadataDeviations"
          name="confirmOrderMetadataDeviations"
        />
        <span>{{ 'AI_PARSER_METADATA_CONFIRM_LABEL' | translate }}</span>
      </label>
      <div class="field-help">{{ 'AI_PARSER_METADATA_CONFIRM_HELP' | translate }}</div>
    </div>

    <lib-gung-tabset>
      <lib-gung-tab [title]="'AI_PARSER_METADATA_TAB_HEADER' | translate">
        <!-- The tabset renders this after the tab's own label, so the dot marks a tab whose
             fields need a look without the reviewer having to open it first. -->
        <ng-template libGungTabTitle>
          <span
            *ngIf="headerTabNeedsAttention"
            class="deviation-dot metadata-tab-dot"
            role="img"
            [attr.aria-label]="'AI_PARSER_METADATA_TAB_ATTENTION' | translate"
            [ngbTooltip]="'AI_PARSER_METADATA_TAB_ATTENTION' | translate"
            tooltipClass="ai-parser-tooltip"
          ></span>
        </ng-template>
        <ng-template libGungTabContent>
          <div class="metadata-grid">
            <ng-container *ngFor="let field of metadataHeaderFields">
              <ng-container *ngTemplateOutlet="metadataField; context: { $implicit: field }"></ng-container>
              <!-- Beside the reference no., in the grid cell the full-width message leaves free. -->
              <div class="def-field ai-parser-field" *ngIf="field.property === 'customerOrderId' && senderEmailEnabled">
                <label class="def-label" for="senderEmail">{{ 'AI_PARSER_SENDER_EMAIL' | translate }}</label>
                <select
                  id="senderEmail"
                  class="form-select form-select-sm"
                  [ngModel]="senderEmailOther ? senderEmailOtherValue : senderEmail"
                  (ngModelChange)="onSenderEmailSelected($event)"
                  [disabled]="isReadOnly"
                >
                  <option value=""></option>
                  <option *ngFor="let email of senderEmailSelectOptions" [value]="email">{{ email }}</option>
                  <option [value]="senderEmailOtherValue">{{ 'AI_PARSER_SENDER_EMAIL_OTHER' | translate }}</option>
                </select>
                <input
                  *ngIf="senderEmailOther"
                  type="email"
                  autocomplete="off"
                  data-bwignore="true"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  class="form-control form-control-sm mt-1"
                  [(ngModel)]="senderEmail"
                  (ngModelChange)="hasPendingChanges = true; editVersion = editVersion + 1"
                  [readonly]="isReadOnly"
                />
              </div>
            </ng-container>
          </div>
        </ng-template>
      </lib-gung-tab>

      <lib-gung-tab [title]="'AI_PARSER_METADATA_TAB_DELIVERY_ADDRESS' | translate">
        <ng-template libGungTabTitle>
          <span
            *ngIf="deliveryAddressTabNeedsAttention"
            class="deviation-dot metadata-tab-dot"
            role="img"
            [attr.aria-label]="'AI_PARSER_METADATA_TAB_ATTENTION' | translate"
            [ngbTooltip]="'AI_PARSER_METADATA_TAB_ATTENTION' | translate"
            tooltipClass="ai-parser-tooltip"
          ></span>
        </ng-template>
        <ng-template libGungTabContent>
          <!-- The address matches no registered delivery location; the backend refuses without this. -->
          <div *ngIf="unregisteredAddress && !reviewFinalized" class="deviation-bar metadata-confirm">
            <label class="commit-override">
              <input
                type="checkbox"
                [(ngModel)]="confirmUnregisteredAddress"
                name="confirmUnregisteredAddress"
              />
              <span>{{ 'AI_PARSER_ADDRESS_UNREGISTERED_CONFIRM' | translate }}</span>
            </label>
            <div class="field-help">{{ 'AI_PARSER_ADDRESS_UNREGISTERED' | translate }}</div>
          </div>

          <div class="metadata-grid">
            <div class="metadata-grid-wide" *ngIf="deliveryLocations.length > 1">
              <!-- The customer's registered delivery locations. Picking one fills the address in;
                   correcting an address here never writes back to the customer's master data. -->
              <div class="def-field ai-parser-field">
                <label class="def-label" for="ai-parser-delivery-location">
                  {{ 'AI_PARSER_METADATA_DELIVERY_LOCATION' | translate }}
                </label>
                <select
                  id="ai-parser-delivery-location"
                  class="form-select form-select-sm"
                  [ngModel]="selectedDeliveryLocationCode"
                  (ngModelChange)="onDeliveryLocationPicked($event)"
                  [disabled]="isReadOnly"
                >
                  <option value=""></option>
                  <option *ngFor="let location of deliveryLocations" [value]="location.deliveryLocationCode">
                    {{ location.name }} - {{ location.address1
                    }}{{ location.city ? ', ' + location.city : '' }}
                  </option>
                </select>
              </div>
            </div>
            <fieldset class="metadata-fieldset" [disabled]="!editDeliveryAddress">
              <ng-container *ngFor="let field of metadataAddressFields">
                <ng-container *ngTemplateOutlet="metadataField; context: { $implicit: field }"></ng-container>
              </ng-container>
            </fieldset>
            <div class="metadata-grid-wide">
              <label class="metadata-edit-address">
                <input
                  type="checkbox"
                  name="editDeliveryAddress"
                  [(ngModel)]="editDeliveryAddress"
                  [disabled]="isReadOnly"
                />
                <span>{{ 'AI_PARSER_METADATA_EDIT_ADDRESS' | translate }}</span>
              </label>
            </div>
          </div>
        </ng-template>
      </lib-gung-tab>
    </lib-gung-tabset>
      </div>
    </div>
  </section>
</ng-template>

<!-- One order-metadata control: label with its signals, the input, and the discarded note. -->
<ng-template #metadataField let-field>
  <div
    *ngIf="(!field.codedOnly || metadataCodedField(field.property)) && (!field.mappedOnly || externalReferenceEnabled)"
    class="def-field ai-parser-field"
    [class.metadata-grid-wide]="field.wide"
    [class.differs-from-document]="!!metadataDocumentValue(field.property)"
  >
    <label class="def-label" [attr.for]="'ai-parser-meta-' + field.property">
      {{ field.labelKey | translate }}
      <lib-pdf-ai-parser-source-glyph [source]="metadataSource(field.property)"></lib-pdf-ai-parser-source-glyph>
      <span
        *ngIf="metadataDeviates(field.property)"
        class="deviation-dot"
        [ngbTooltip]="'AI_PARSER_METADATA_DEVIATES_UNKNOWN' | translate"
        tooltipClass="ai-parser-tooltip"
      ></span>
    </label>
    <!-- Coded against this installation's master data: pick from the list. Anything else, and
         anything whose configured table this installation carries no rows for, is free text. -->
    <lib-meta-select-input
      *ngIf="metadataCodedField(field.property) as coded; else metadataTextInput"
      [suppressLabel]="true"
      [table]="coded.table"
      [metaId]="coded.codeField"
      [metaDisplay]="coded.displayField"
      [showId]="true"
      extraInputClass="form-control-sm"
      [disabled]="isReadOnly"
      [emptyOptionLabel]="
        field.property === 'stockId' && defaultStockId
          ? ('AI_PARSER_METADATA_STOCK_DEFAULT' | translate: { stockId: defaultStockId })
          : ''
      "
      [(ngModel)]="orderMetadata[field.property]"
      (ngModelChange)="onOrderMetadataChanged(field.property)"
    ></lib-meta-select-input>
    <ng-template #metadataTextInput>
      <textarea
        *ngIf="field.multiline; else metadataSingleLine"
        [attr.id]="'ai-parser-meta-' + field.property"
        class="form-control form-control-sm"
        rows="3"
        [(ngModel)]="orderMetadata[field.property]"
        (ngModelChange)="onOrderMetadataChanged()"
        [readonly]="isReadOnly"
      ></textarea>
      <ng-template #metadataSingleLine>
        <input
          type="text"
          autocomplete="off"
          data-bwignore="true"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          [attr.id]="'ai-parser-meta-' + field.property"
          class="form-control form-control-sm"
          [(ngModel)]="orderMetadata[field.property]"
          (ngModelChange)="onOrderMetadataChanged()"
          [readonly]="isReadOnly"
        />
      </ng-template>
    </ng-template>
    <!-- What the document stated, when the field holds something else: one click takes it,
         the same stance the rows table asks for on a deviating price. -->
    <button
      *ngIf="metadataDocumentValue(field.property) as documentValue"
      type="button"
      class="metadata-document"
      [disabled]="isReadOnly"
      [ngbTooltip]="'AI_PARSER_METADATA_USE_DOCUMENT_VALUE' | translate"
      tooltipClass="ai-parser-tooltip"
      (click)="useDocumentMetadata(field.property)"
    >
      {{ 'AI_PARSER_METADATA_DOCUMENT_VALUE' | translate: { value: documentValue } }}
    </button>
    <div
      *ngIf="metadataDiscarded(field.property) as discarded"
      class="metadata-discarded"
      [ngbTooltip]="'AI_PARSER_METADATA_DISCARDED_TOOLTIP' | translate: { value: discarded }"
      tooltipClass="ai-parser-tooltip"
    >
      <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
      <span>{{ 'AI_PARSER_METADATA_DISCARDED' | translate: { value: discarded } }}</span>
    </div>
    <div class="field-help" *ngFor="let warning of warningsFor(field.property)">{{ warningText(warning) }}</div>
  </div>
</ng-template>

<!-- Shared editable order rows table + per-mode actions -->
<ng-template #rowsTable>
  <div *ngIf="editableRows.length > 0" class="order-rows">
    <!-- Table toolbar (§6): row count and validation summary; actions sit under the table -->
    <div class="rows-toolbar">
      <div class="rows-toolbar-left">
        <span class="rows-heading">
          {{
            (editableRows.length === 1 ? 'AI_PARSER_SUMMARY_ROWS_ONE' : 'AI_PARSER_SUMMARY_ROWS')
              | translate: { count: editableRows.length }
          }}
        </span>
        <!-- Validation summary: shares the toolbar line with the row count -->
        <span *ngIf="isValidated && !rowStateKnown" class="validation-summary">
          <span>{{ 'AI_PARSER_SELECT_CUSTOMER_FOR_PRICE' | translate }}</span>
        </span>
        <span *ngIf="isValidated && rowStateKnown" class="validation-summary">
          <span [class.summary-invalid]="invalidRowCount > 0">{{
            (invalidRowCount === 0 ? 'AI_PARSER_SUMMARY_ALL_VALID' : 'AI_PARSER_SUMMARY_VALID_OF_TOTAL')
              | translate: { valid: validRowCount, total: editableRows.length }
          }}</span>
          <ng-container *ngIf="unresolvedPriceDeviationCount > 0">
            <span class="summary-sep" aria-hidden="true">·</span>
            <a href="#" class="summary-deviations" (click)="$event.preventDefault(); scrollToFirstUnresolvedRow()">
              {{
                (unresolvedPriceDeviationCount === 1
                  ? 'AI_PARSER_DEVIATIONS_TO_RESOLVE_ONE'
                  : 'AI_PARSER_DEVIATIONS_TO_RESOLVE'
                ) | translate: { count: unresolvedPriceDeviationCount }
              }}
            </a>
          </ng-container>
        </span>
      </div>
      <!-- Shown once there is something to filter, and kept while on so it can be switched back off -->
      <label *ngIf="isValidated && (rowsToFixCount > 0 || onlyRowsToFix)" class="rows-filter">
        <input
          class="pill-switch"
          type="checkbox"
          role="switch"
          [ngModel]="onlyRowsToFix"
          (ngModelChange)="setOnlyRowsToFix($event)"
        />
        <span>{{ 'AI_PARSER_ONLY_ROWS_TO_FIX' | translate: { count: rowsToFixCount } }}</span>
      </label>
    </div>

    <!-- Price deviations (§4): resolve in bulk, override per row in the price cells -->
    <div *ngIf="unresolvedPriceDeviationCount > 0" class="deviation-bar">
      <span class="deviation-line">
        {{
          (priceDeviationCount === 1 ? 'AI_PARSER_DEVIATION_BAR_ONE' : 'AI_PARSER_DEVIATION_BAR')
            | translate: { count: priceDeviationCount }
        }}
      </span>
      <div class="deviation-bar-actions">
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="applyBulkPriceChoice('document')">
          {{ 'AI_PARSER_USE_DOCUMENT_PRICES' | translate }}
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="applyBulkPriceChoice('customer')">
          {{ 'AI_PARSER_USE_CUSTOMER_PRICES' | translate }}
        </button>
      </div>
    </div>

    <div *ngIf="priceDeviationCount > 0 && unresolvedPriceDeviationCount === 0" class="deviation-resolved">
      <span class="deviation-line">
        <i class="fas fa-check" aria-hidden="true"></i>
        {{
          'AI_PARSER_DEVIATIONS_RESOLVED'
            | translate
              : { count: priceDeviationCount, document: documentPriceChoiceCount, customer: customerPriceChoiceCount }
        }}
      </span>
      <div class="deviation-bar-actions">
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="resetPriceChoices()">
          {{ 'AI_PARSER_CHANGE' | translate }}
        </button>
      </div>
    </div>

    <div class="table-responsive rows-table-wrapper">
      <table class="table table-sm editable-table">
        <colgroup>
          <col class="col-marker" />
          <col class="col-product" />
          <col class="col-qty" />
          <col class="col-stock" *ngIf="showStock" />
          <col class="col-doc-price" />
          <col class="col-cust-price" />
          <col class="col-remove" />
        </colgroup>
        <thead>
          <tr>
            <th class="marker-col"><span class="marker-col-label">#</span></th>
            <th class="product-col">{{ 'PRODUCT' | translate }}</th>
            <th class="text-end qty-col">{{ 'AI_PARSER_QUANTITY' | translate }}</th>
            <th class="text-end stock-col" *ngIf="showStock">{{ 'STOCK' | translate }}</th>
            <th class="text-end price-col">
              {{ 'AI_PARSER_DOC_PRICE' | translate
              }}<span *ngIf="rowsCurrencyCode" class="col-unit"> ({{ rowsCurrencyCode }})</span>
            </th>
            <th class="text-end price-col">
              {{ 'AI_PARSER_CUSTOMER_PRICE' | translate
              }}<span *ngIf="rowsCurrencyCode" class="col-unit"> ({{ rowsCurrencyCode }})</span>
              <span *ngIf="isLoadingPrices" class="spinner-border spinner-border-sm ms-1"></span>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of editableRows; let i = index; trackBy: trackByIndex">
          <tr
            *ngIf="isRowShown(row)"
            [style.--row-i]="i"
            [attr.id]="'ai-parser-row-' + i"
            [class.row-invalid]="rowSeverity(row) === 'error'"
            [class.row-warning]="rowSeverity(row) === 'warning'"
          >
            <!-- Row marker: number on the control line, validation glyph under it -->
            <td class="marker-cell">
              <span class="marker-num">{{ i + 1 }}</span>
              <span class="state-glyph">
                <i
                  *ngIf="rowSeverity(row) === 'error'"
                  class="fas fa-circle-xmark glyph-danger"
                  [ngbTooltip]="rowFaultText(row)"
                  tooltipClass="ai-parser-tooltip"
                  [attr.aria-label]="rowFaultText(row)"
                  role="img"
                ></i>
                <i
                  *ngIf="rowSeverity(row) === 'warning'"
                  class="fas fa-triangle-exclamation glyph-warning"
                  [ngbTooltip]="rowFaultText(row)"
                  tooltipClass="ai-parser-tooltip"
                  [attr.aria-label]="rowFaultText(row)"
                  role="img"
                ></i>
                <span
                  *ngIf="row.isValid !== false && needsPriceStance(row) && !row.priceChoice"
                  class="deviation-dot"
                  [ngbTooltip]="'AI_PARSER_DEVIATION_DOT_TOOLTIP' | translate"
                  tooltipClass="ai-parser-tooltip"
                  placement="right"
                ></span>
                <i
                  *ngIf="rowStateKnown && row.isValid === true && rowSeverity(row) == null && !(needsPriceStance(row) && !row.priceChoice)"
                  class="fas fa-circle-check glyph-success"
                  aria-hidden="true"
                ></i>
              </span>
            </td>
            <td class="product-cell">
              <!-- Search field and product identity in one control: typing searches by id or
                   name, the title and the arrow open the product page in a new tab, and neither
                   steals the caret from someone mid-edit. -->
              <div
                class="product-field"
                [class.product-field--invalid]="fieldSeverity(row, 'product') === 'error'"
                [class.product-field--warning]="fieldSeverity(row, 'product') === 'warning'"
              >
                <label class="product-field-main" [attr.for]="'ai-parser-sku-' + i">
                  <!-- Why the row failed and what the document printed, so the line can be found in the PDF -->
                  <span *ngIf="showProductFault(row); else productTitle" class="product-fault">
                    <span class="product-fault-reason">{{ productFaultReason(row) }}</span>
                    <span *ngIf="faultDocumentProductId(row) as documentProductId" class="product-doc-value">{{
                      documentProductId
                    }}</span>
                    <ng-container *ngIf="faultDocumentEan(row) as documentEan">
                      <span *ngIf="faultDocumentProductId(row)" class="product-doc-sep" aria-hidden="true">·</span>
                      <span class="product-doc-pair">
                        <span class="product-doc-label">EAN</span>
                        <span class="product-doc-value">{{ documentEan }}</span>
                      </span>
                    </ng-container>
                  </span>
                  <ng-template #productTitle>
                    <span class="product-title" [attr.title]="row.product?.name">{{ row.product?.name || '—' }}</span>
                  </ng-template>
                  <span class="product-id-row">
                    <span class="product-id-label" aria-hidden="true">SKU</span>
                    <i *ngIf="!isReadOnly" class="fas fa-magnifying-glass product-id-search" aria-hidden="true"></i>
                    <input
                      (focus)="$event.target.select()"
                      type="text"
                      autocomplete="off"
                      data-bwignore="true"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      [attr.id]="'ai-parser-sku-' + i"
                      class="form-control form-control-sm product-id-input"
                      [(ngModel)]="row.productId"
                      [placeholder]="'AI_PARSER_PRODUCT_SEARCH_PLACEHOLDER' | translate"
                      (ngModelChange)="onRowProductIdChanged(row)"
                      [readonly]="isReadOnly"
                      [ngbTypeahead]="searchLocalProducts"
                      [resultFormatter]="productSearchFormatter"
                      [inputFormatter]="productIdFormatter"
                      (selectItem)="onProductPicked(row, $event)"
                      [attr.aria-label]="'AI_PARSER_PRODUCT_ID' | translate"
                    />
                    <span *ngIf="showEanChip(row)" class="product-ean" tabindex="0" [ngbTooltip]="row.ean" [attr.aria-label]="'EAN ' + row.ean">EAN</span>
                    <span
                      *ngIf="row.customerProductId"
                      class="product-ean product-customer-id"
                      [class.product-customer-id--matched]="isMatchedByCustomerProductId(row)"
                      tabindex="0"
                      [ngbTooltip]="customerProductIdTooltip(row)"
                      [attr.aria-label]="customerProductIdTooltip(row)"
                      >{{ 'AI_PARSER_CUSTOMER_PRODUCT_ID_CHIP' | translate }}</span
                    >
                  </span>
                  <span
                    *ngIf="showDocumentOrigin(row)"
                    class="product-origin"
                    [ngbTooltip]="documentOriginText(row)"
                    container="body"
                  >
                    {{ 'AI_PARSER_FROM_DOCUMENT' | translate }}:
                    <span *ngIf="row.documentProductId" class="product-doc-value">{{ row.documentProductId }}</span>
                    <ng-container *ngIf="originDocumentEan(row) as documentEan">
                      <span *ngIf="row.documentProductId" class="product-doc-sep" aria-hidden="true">·</span>
                      EAN <span class="product-doc-value">{{ documentEan }}</span>
                    </ng-container>
                  </span>
                </label>
                <!-- Kept in the layout instead of *ngIf'd away: every keystroke in the id drops
                     row.product, and a slot that collapses and comes back shifts the name and the
                     input beside it on each character typed. -->
                <a
                  class="product-open"
                  [class.product-open--absent]="!row.product"
                  [routerLink]="row.product ? ['/product/', row.product.id] : null"
                  target="_blank"
                  [ngbTooltip]="'PRODUCT_DETAILS' | translate"
                  [attr.aria-label]="'PRODUCT_DETAILS' | translate"
                >
                  <i class="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
                </a>
              </div>
              <div *ngIf="showCandidates(row)" class="product-candidates">
                <span class="product-candidates-label">{{ 'AI_PARSER_CUSTOMER_PRODUCT_ID_CANDIDATES' | translate }}</span>
                <button
                  *ngFor="let candidate of row.customerProductIdCandidates"
                  type="button"
                  class="btn btn-sm btn-outline-primary product-candidate"
                  (click)="onCandidatePicked(row, candidate)"
                >
                  {{ candidate.productId }}<span *ngIf="candidate.name"> — {{ candidate.name }}</span>
                </button>
              </div>
            </td>
            <td class="qty-cell">
              <input
                type="number"
                class="form-control form-control-sm qty-input"
                [class.is-invalid]="fieldSeverity(row, 'quantity') === 'error'"
                [class.is-warning]="fieldSeverity(row, 'quantity') === 'warning'"
                [(ngModel)]="row.quantity"
                min="1"
                step="1"
                (ngModelChange)="onRowQuantityChanged()"
                [readonly]="isReadOnly"
              />
            </td>
            <!-- Read-only stock, read against this row's quantity: plain grey when it covers
                 the order, amber when it falls short, red at zero. Never a tab stop, never
                 empty — an unresolved product still gets a placeholder. -->
            <td class="stock-cell" *ngIf="showStock" tabindex="-1">
              <ng-container *ngIf="row.product && row.availability; else noStock">
                <span
                  class="stock-figure"
                  [class.stock-figure--out]="row.availability.currentAvailability <= 0"
                  [class.stock-figure--short]="
                    row.availability.currentAvailability > 0 && availabilityState(row) !== 'ok'
                  "
                  [ngbTooltip]="rowStockTooltip(row)"
                  tooltipClass="ai-parser-tooltip"
                  >{{ row.availability.currentAvailability | number: '1.0-0' }}</span
                >
              </ng-container>
              <ng-template #noStock><span class="cell-empty">—</span></ng-template>
            </td>
            <!-- Document vs customer price; deviating rows resolve by clicking a price -->
            <td class="price-cell text-end">
              <div class="price-line" *ngIf="row.documentUnitPrice != null; else noDocPrice">
                <button
                  *ngIf="row.priceDeviates && !isReadOnly; else plainDocPrice"
                  type="button"
                  class="price-option"
                  [class.price-invalid]="fieldSeverity(row, 'docPrice') === 'error'"
                  [ngbTooltip]="fieldFaultText(row, 'docPrice')"
                  tooltipClass="ai-parser-tooltip"
                  [disableTooltip]="!fieldSeverity(row, 'docPrice')"
                  [class.price-chosen]="row.priceChoice === 'document'"
                  [class.price-unchosen]="!!row.priceChoice && row.priceChoice !== 'document'"
                  [attr.aria-label]="'AI_PARSER_USE_THIS_PRICE' | translate"
                  (click)="setPriceChoice(row, 'document')"
                >
                  <span class="price-value"
                    >{{ row.documentUnitPrice | number: '1.2-2'
                    }}<ng-container *ngIf="!rowsCurrencyCode"> {{ row.currencyCode }}</ng-container></span
                  >
                </button>
                <ng-template #plainDocPrice>
                  <span class="price-value" [class.price-static-chosen]="row.priceChoice === 'document'"
                    >{{ row.documentUnitPrice | number: '1.2-2'
                    }}<ng-container *ngIf="!rowsCurrencyCode"> {{ row.currencyCode }}</ng-container></span
                  >
                </ng-template>
              </div>
              <ng-template #noDocPrice>
                <span class="cell-empty">—</span>
              </ng-template>
            </td>
            <td class="price-cell text-end">
              <div class="price-line" *ngIf="effectiveCustomerPrice(row) != null || !isReadOnly">
                <!-- How far the chosen document price sits from this customer price: shown here,
                     against the figure it deviates from, rather than crammed beside the document
                     price in a column half this one's width. -->
                <span
                  *ngIf="manualPriceChanged(row) && priceDeltaPercent(row.customerUnitPrice, row.manualUnitPrice) as delta"
                  class="price-delta"
                  [attr.title]="originalCustomerPriceHint(row)"
                  [class.price-delta--below]="priceDeltaIsBelow(row.customerUnitPrice, row.manualUnitPrice)"
                  [class.price-delta--above]="priceDeltaIsBelow(row.customerUnitPrice, row.manualUnitPrice) === false"
                >
                  {{ delta }}
                </span>
                <span
                  *ngIf="row.priceChoice === 'document' && priceDeltaPercent(row.customerUnitPrice, row.documentUnitPrice) as discount"
                  class="price-delta"
                  [class.price-delta--below]="priceDeltaIsBelow(row.customerUnitPrice, row.documentUnitPrice)"
                  [class.price-delta--above]="priceDeltaIsBelow(row.customerUnitPrice, row.documentUnitPrice) === false"
                >
                  {{ discount }}
                </span>
                <span *ngIf="!isReadOnly; else plainCustomerPrice" class="price-edit-wrap">
                  <input
                    type="number"
                    step="0.01"
                    class="form-control form-control-sm price-input"
                    [class.is-invalid]="fieldSeverity(row, 'custPrice') === 'error'"
                    [class.is-warning]="fieldSeverity(row, 'custPrice') === 'warning'"
                    [class.price-chosen]="row.priceChoice === 'customer' || row.priceChoice === 'manual'"
                    [class.price-unchosen]="!!row.priceChoice && row.priceChoice === 'document'"
                    [ngModel]="effectiveCustomerPrice(row)"
                    [disabled]="row.productLookupDone && !row.product"
                    [attr.placeholder]="!rowStateKnown ? ('AI_PARSER_SELECT_CUSTOMER_FOR_PRICE' | translate) : null"
                    (focus)="$event.target.select()"
                    (click)="row.priceChoice !== 'manual' && effectiveCustomerPrice(row) != null && setPriceChoice(row, 'customer')"
                    (ngModelChange)="onCustomerPriceEdited(row, $event)"
                    [attr.aria-label]="'AI_PARSER_CUSTOMER_PRICE' | translate"
                  />
                  <i class="fas fa-pen price-edit-icon" aria-hidden="true"></i>
                </span>
                <ng-template #plainCustomerPrice>
                  <span *ngIf="effectiveCustomerPrice(row) != null; else noCustomerPrice" class="price-value" [class.price-static-chosen]="row.priceChoice === 'customer' || row.priceChoice === 'manual'"
                    >{{ effectiveCustomerPrice(row) | number: '1.2-2'
                    }}<ng-container *ngIf="!rowsCurrencyCode"> {{ row.currencyCode }}</ng-container></span
                  >
                </ng-template>
              </div>
              <ng-template #noCustomerPrice>
                <span class="cell-empty">—</span>
              </ng-template>
            </td>
            <td class="remove-cell">
              <button
                *ngIf="!isReadOnly"
                type="button"
                class="btn btn-link btn-sm row-remove"
                (click)="removeRow(i)"
                [attr.aria-label]="'AI_PARSER_REMOVE_ROW' | translate"
              >
                <i class="fas fa-xmark" aria-hidden="true"></i>
              </button>
            </td>
          </tr>
          </ng-container>
        </tbody>
      </table>
    </div>

    <div class="add-row-row">
      <button type="button" class="soft-btn" *ngIf="!isReadOnly" (click)="addRow()">
        <i class="fas fa-plus me-1" aria-hidden="true"></i>{{ 'AI_PARSER_ADD_ROW' | translate }}
      </button>
    </div>

    <div class="rows-actions">
      <div class="rows-stock">
        <ng-container *ngTemplateOutlet="metadataField; context: { $implicit: metadataStockField }"></ng-container>
      </div>
      <ng-container *ngIf="hasPendingChanges || isValidating || !isValidated; else upToDate">
        <button
          type="button"
          class="soft-btn soft-btn--primary"
          [disabled]="isValidating || deliveryDateInvalid"
          (click)="validate()"
          [ngbTooltip]="'AI_PARSER_VALIDATE_SAVE_TOOLTIP' | translate"
        >
          <span *ngIf="isValidating" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
          {{ validateButtonLabel }}
        </button>
      </ng-container>
      <ng-template #upToDate>
        <span class="rows-checked">
          <i class="fas fa-check" aria-hidden="true"></i>{{ 'AI_PARSER_UP_TO_DATE' | translate }}
        </span>
      </ng-template>
    </div>

    <!-- A fault no override reaches: say so once, in full, and turn the commit controls off. -->
    <div *ngIf="hasBlockingFault" class="parser-note note-danger commit-blocker">
      <i class="fas fa-circle-xmark" aria-hidden="true"></i>
      <span>{{ blockingFaultMessage }}</span>
    </div>

    <!-- Rows the reviewer may wave through: the override lives with the problem it waives. -->
    <div *ngIf="hasOverridableInvalidRows && !reviewFinalized" class="parser-note note-warning commit-override-note">
      <span class="commit-override-text">
        <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
        {{
          (invalidRowCount === 1 ? 'AI_PARSER_SUMMARY_INVALID_ONE' : 'AI_PARSER_SUMMARY_INVALID')
            | translate: { count: invalidRowCount }
        }}
      </span>
      <label class="commit-override">
        <input type="checkbox" [(ngModel)]="overrideInvalidRows" name="overrideInvalidRows" />
        <span>{{ 'AI_PARSER_SUBMIT_OVERRIDE_LABEL' | translate }}</span>
      </label>
    </div>

    <!-- Region D — sticky action bar -->
    <div class="commit-bar">
      <div class="commit-rollup">
        <span class="rollup-stats">
          {{
            (editableRows.length === 1 ? 'AI_PARSER_SUMMARY_ROWS_ONE' : 'AI_PARSER_SUMMARY_ROWS')
              | translate: { count: editableRows.length }
          }}
          <span class="summary-sep" aria-hidden="true">·</span>
          {{
            (totalUnits === 1 ? 'AI_PARSER_SUMMARY_UNITS_ONE' : 'AI_PARSER_SUMMARY_UNITS')
              | translate: { count: totalUnits }
          }}
          <ng-container *ngIf="reviewOrderTotal as total">
            <span class="summary-sep" aria-hidden="true">·</span>
            <span class="rollup-total" [class.rollup-total--partial]="total.partial">
              <span *ngIf="total.partial" aria-hidden="true">≥ </span
              >{{ total.value | number: '1.2-2' }} {{ total.currencyCode }}
            </span>
          </ng-container>
        </span>
      </div>
      <span class="commit-hint" aria-live="polite">{{ commitBlockedReason }}</span>

      <div class="commit-actions">
        <!-- Reject + submit to ERP. Hidden once finalized, or while the ERP outcome is unresolved. -->
        <ng-container *ngIf="!reviewFinalized && reviewItem?.status !== 'SUBMIT_OUTCOME_UNKNOWN'">
          <button
            type="button"
            class="btn btn-outline-secondary commit-reject"
            [disabled]="isRejecting || isSubmittingToErp"
            (click)="openRejectModal()"
          >
            {{ 'AI_PARSER_REJECT' | translate }}…
          </button>
          <span class="d-inline-block" [ngbTooltip]="submitToErpTooltip">
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="isSubmittingToErp || isPreparingSubmit || !canSubmitToErp"
              (click)="confirmSubmitToErp()"
            >
              <span *ngIf="isSubmittingToErp || isPreparingSubmit" class="spinner-border spinner-border-sm me-1"></span>
              {{ (isSubmittingToErp ? 'AI_PARSER_SUBMITTING' : 'AI_PARSER_SUBMIT') | translate }}
            </button>
          </span>
        </ng-container>
      </div>
    </div>
  </div>

  <div *ngIf="editableRows.length === 0" class="parser-note note-warning">
    {{ 'AI_PARSER_NO_ROWS_FOUND' | translate }}
    <button type="button" class="btn btn-link btn-sm p-0 ms-1" *ngIf="!isReadOnly" (click)="addRow()">
      {{ 'AI_PARSER_ADD_ROW' | translate }}
    </button>
  </div>
</ng-template>

<!-- Reject reason modal -->
<ng-template #rejectModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ 'AI_PARSER_REJECT_ORDER' | translate }}</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()" [attr.aria-label]="'CANCEL' | translate"></button>
  </div>
  <div class="modal-body">
    <label class="form-label" for="rejectionReason">{{ 'AI_PARSER_REJECT_REASON' | translate }}</label>
    <textarea
      id="rejectionReason"
      class="form-control"
      rows="3"
      [(ngModel)]="rejectionReason"
      [placeholder]="'AI_PARSER_REJECT_REASON_PLACEHOLDER' | translate"
    ></textarea>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-link text-secondary" (click)="modal.dismiss()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="btn btn-outline-danger" (click)="modal.close()">
      {{ 'AI_PARSER_REJECT' | translate }}
    </button>
  </div>
</ng-template>

<!-- Manual resolution for SUBMIT_OUTCOME_UNKNOWN, after the reviewer checked the ERP -->
<ng-template #resolveOutcomeModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ 'AI_PARSER_RESOLVE_OUTCOME_TITLE' | translate }}</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()" [attr.aria-label]="'CANCEL' | translate"></button>
  </div>
  <div class="modal-body">
    <p>{{ 'AI_PARSER_RESOLVE_OUTCOME_BODY' | translate }}</p>
    <label class="form-label" for="resolveErpOrderId">{{ 'AI_PARSER_RESOLVE_ERP_ORDER_ID' | translate }}</label>
    <input
      id="resolveErpOrderId"
      type="text"
      autocomplete="off"
      data-bwignore="true"
      data-1p-ignore
      data-lpignore="true"
      data-form-type="other"
      class="form-control"
      [(ngModel)]="resolveErpOrderId"
      [placeholder]="'AI_PARSER_RESOLVE_ERP_ORDER_ID_PLACEHOLDER' | translate"
    />
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-link text-secondary" (click)="modal.dismiss()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="btn btn-outline-secondary" (click)="modal.close('not-posted')">
      {{ 'AI_PARSER_RESOLVE_NOT_POSTED' | translate }}
    </button>
    <button type="button" class="btn btn-danger" [disabled]="!resolveErpOrderIdValid" (click)="modal.close('posted')">
      {{ 'AI_PARSER_RESOLVE_POSTED' | translate }}
    </button>
  </div>
</ng-template>

<!-- Delete configuration confirm modal (replaces window.confirm) -->
<ng-template #deleteConfigModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">{{ 'AI_PARSER_CONFIG_DELETE' | translate }}</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    {{ 'AI_PARSER_CONFIG_DELETE_CONFIRM' | translate: { name: selectedConfigName } }}
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-link text-secondary" (click)="modal.dismiss()">
      {{ 'CANCEL' | translate }}
    </button>
    <button type="button" class="btn btn-outline-danger" (click)="modal.close()">
      {{ 'AI_PARSER_CONFIG_DELETE' | translate }}
    </button>
  </div>
</ng-template>
